import { z } from 'zod';
import { SuggestionStatus } from './support';

/*
 * Анализ предложений и премирование за предложения и активность (раздел 6.22: БЗ-32…БЗ-35).
 *  - Анализ: сводка за период, разделы, месяцы, что ждёт решения, что делать первым, повторы, причины отказов.
 *  - Премирование: правила компании (баллы, период, номинации и суммы), баллы сотрудников из записей портала,
 *    итоги периода с победителями — решение принимает человек, итоги фиксируются и не пересчитываются.
 */

const Id = z.uuid();
const Ts = z.string();
const Day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата в виде ГГГГ-ММ-ДД')
  .refine(
    (d) => !Number.isNaN(Date.parse(d)) && new Date(d).toISOString().slice(0, 10) === d,
    'Такой даты нет',
  );

/* ───────────────────────── Анализ предложений (БЗ-32, БЗ-33) ───────────────────────── */

/** Срок разбора по умолчанию: владелец разбирает предложения раз в две недели (БЗ-29) */
export const REVIEW_DAYS_DEFAULT = 14;

export const AnalysisItem = z.object({
  id: Id,
  number: z.string(),
  title: z.string(),
  status: SuggestionStatus,
  /** Раздел портала по странице, с которой подано предложение */
  section: z.string(),
  authorName: z.string(),
  votes: z.number().int(),
  benefit: z.number().int().nullable(),
  effort: z.number().int().nullable(),
  priority: z.number().int(),
  /** Сколько дней прошло с подачи */
  ageDays: z.number().int(),
  createdAt: Ts,
});
export type AnalysisItem = z.infer<typeof AnalysisItem>;

export const AnalysisSection = z.object({
  section: z.string(),
  /** Подано за период */
  total: z.number().int(),
  /** Открыто сейчас: на рассмотрении, в плане или в разработке */
  open: z.number().int(),
  released: z.number().int(),
  rejected: z.number().int(),
  votes: z.number().int(),
});
export type AnalysisSection = z.infer<typeof AnalysisSection>;

export const AnalysisMonth = z.object({
  /** ГГГГ-ММ */
  month: z.string(),
  created: z.number().int(),
  /** Первое решение по предложению: взято в работу, выпущено или отклонено */
  decided: z.number().int(),
  released: z.number().int(),
});
export type AnalysisMonth = z.infer<typeof AnalysisMonth>;

export const AnalysisRejection = z.object({
  id: Id,
  number: z.string(),
  title: z.string(),
  reason: z.string(),
  at: Ts,
});
export type AnalysisRejection = z.infer<typeof AnalysisRejection>;

export const SuggestionAnalysis = z.object({
  days: z.number().int(),
  from: Day,
  to: Day,
  /** Срок разбора: предложения на рассмотрении дольше этого — «ждут решения» */
  reviewDays: z.number().int(),
  totals: z.object({
    /** Подано за период, без повторов */
    created: z.number().int(),
    /** На рассмотрении сейчас и из них — дольше срока разбора */
    review: z.number().int(),
    waitingLong: z.number().int(),
    /** Открыто сейчас: на рассмотрении, в плане, в разработке */
    open: z.number().int(),
    /** Решения за период: взято в план или разработку, выпущено, отклонено, объединено как повтор */
    accepted: z.number().int(),
    released: z.number().int(),
    rejected: z.number().int(),
    duplicates: z.number().int(),
    /** Доля принятых среди решённых за период, %; null — решений не было */
    acceptedPercent: z.number().int().nullable(),
    /** Среднее время от подачи до первого решения, дней; null — решений не было */
    avgDecisionDays: z.number().nullable(),
    /** Авторов и голосов коллег за период */
    authors: z.number().int(),
    votes: z.number().int(),
    /** Открытых предложений без оценки пользы и трудоёмкости */
    unassessed: z.number().int(),
  }),
  /** На рассмотрении дольше срока разбора: сначала самые старые */
  waiting: z.array(AnalysisItem),
  /** Открытые предложения по приоритету: что делать первым */
  priorities: z.array(AnalysisItem),
  bySection: z.array(AnalysisSection),
  byMonth: z.array(AnalysisMonth),
  /** Похожие открытые предложения: кандидаты на объединение повторов */
  duplicates: z.array(z.object({ items: z.array(AnalysisItem) })),
  rejections: z.array(AnalysisRejection),
  pilot: z.object({ available: z.boolean() }),
});
export type SuggestionAnalysis = z.infer<typeof SuggestionAnalysis>;

/** Сводка Pilot по открытым предложениям: темы, повторы, что сделать первым (помечена «Подготовлено ИИ») */
export const SuggestionPilotSummary = z.object({
  text: z.string(),
  label: z.string(),
  model: z.string(),
  /** Сколько предложений ушло в вопрос */
  count: z.number().int(),
  at: Ts,
});
export type SuggestionPilotSummary = z.infer<typeof SuggestionPilotSummary>;

/* ───────────────────────── Премирование (БЗ-34, БЗ-35) ───────────────────────── */

/** За что начисляются баллы: только записи портала (КП-02, КП-05) */
export const REWARD_ACTIONS = {
  submitted: 'Предложение подано',
  accepted: 'Предложение взято в план',
  released: 'Предложение выпущено',
  vote: 'Голос коллеги за предложение',
  bug: 'Ошибка подтверждена и передана разработке',
} as const;
export type RewardAction = keyof typeof REWARD_ACTIONS;
export const RewardAction = z.enum(Object.keys(REWARD_ACTIONS) as [RewardAction, ...RewardAction[]]);
export const REWARD_ACTION_LIST = Object.keys(REWARD_ACTIONS) as RewardAction[];

export const REWARD_PERIOD_KINDS = { month: 'Месяц', quarter: 'Квартал' } as const;
export type RewardPeriodKind = keyof typeof REWARD_PERIOD_KINDS;
export const RewardPeriodKind = z.enum(['month', 'quarter']);

/** Вид номинации: за предложение (победитель — автор предложения) или за активность (по баллам) */
export const REWARD_NOMINATION_KINDS = { suggestion: 'За предложение', activity: 'За активность' } as const;
export type RewardNominationKind = keyof typeof REWARD_NOMINATION_KINDS;
export const RewardNominationKind = z.enum(['suggestion', 'activity']);

const Points = z.number().int().min(0).max(100);
/** Сумма премии одному победителю, в валюте компании */
const Amount = z.number().min(0).max(1_000_000_000);

export const RewardNomination = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[a-z0-9_-]{2,30}$/, 'Код номинации — латинские буквы, цифры, «-» и «_»'),
  title: z.string().trim().min(3, 'Назовите номинацию').max(100),
  kind: RewardNominationKind,
  /** Сумма одному победителю; null — без суммы (благодарность) или сумму назначают при итогах */
  amount: Amount.nullable(),
  /** Сколько победителей в номинации за период */
  winners: z.number().int().min(1).max(5),
});
export type RewardNomination = z.infer<typeof RewardNomination>;

/**
 * Номинация в ответах: у того, кто не утверждает премии, суммы в названии заменены на «сумма скрыта» — название
 * может стать длиннее, чем разрешено при вводе
 */
export const RewardNominationView = RewardNomination.extend({ title: z.string() });

export const RewardRules = z
  .object({
    enabled: z.boolean(),
    period: RewardPeriodKind,
    points: z.object({
      submitted: Points,
      accepted: Points,
      released: Points,
      vote: Points,
      bug: Points,
    }),
    /** Голосов коллег, засчитываемых одному предложению за период */
    voteCap: z.number().int().min(0).max(100),
    /** Срок разбора предложения, дней: дольше — «ждёт решения» в анализе и «Ждёт меня» */
    reviewDays: z.number().int().min(3).max(90),
    nominations: z.array(RewardNomination).min(1, 'Нужна хотя бы одна номинация').max(6),
  })
  .refine((r) => new Set(r.nominations.map((n) => n.code)).size === r.nominations.length, {
    message: 'Коды номинаций повторяются',
    path: ['nominations'],
  });
export type RewardRules = z.infer<typeof RewardRules>;

/** Правила по умолчанию: суммы не заданы — их задаёт владелец портала у себя */
export const REWARD_RULES_DEFAULT: RewardRules = {
  enabled: true,
  period: 'month',
  points: { submitted: 1, accepted: 3, released: 5, vote: 1, bug: 2 },
  voteCap: 10,
  reviewDays: REVIEW_DAYS_DEFAULT,
  nominations: [
    { code: 'best', title: 'Лучшее предложение', kind: 'suggestion', amount: null, winners: 1 },
    { code: 'active', title: 'Самый активный участник', kind: 'activity', amount: null, winners: 1 },
  ],
};

/** Правила для просмотра: суммы номинаций видит только тот, кто утверждает премии; остальным — null */
export const RewardRulesView = z.object({
  enabled: z.boolean(),
  period: RewardPeriodKind,
  points: z.object({
    submitted: z.number().int(),
    accepted: z.number().int(),
    released: z.number().int(),
    vote: z.number().int(),
    bug: z.number().int(),
  }),
  voteCap: z.number().int(),
  reviewDays: z.number().int(),
  nominations: z.array(RewardNominationView),
  amountsHidden: z.boolean(),
});
export type RewardRulesView = z.infer<typeof RewardRulesView>;

const MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

export interface RewardPeriodRange {
  start: string;
  end: string;
  label: string;
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** Период премирования, в который попадает день ГГГГ-ММ-ДД: месяц или квартал целиком */
export function rewardPeriodOf(kind: RewardPeriodKind, day: string): RewardPeriodRange {
  const [y, m] = day.split('-').map(Number) as [number, number];
  if (kind === 'month')
    return {
      start: `${y}-${pad2(m)}-01`,
      end: `${y}-${pad2(m)}-${pad2(lastDay(y, m))}`,
      label: `${MONTHS[m - 1]} ${y}`,
    };
  const q = Math.floor((m - 1) / 3);
  const first = q * 3 + 1;
  const last = first + 2;
  return {
    start: `${y}-${pad2(first)}-01`,
    end: `${y}-${pad2(last)}-${pad2(lastDay(y, last))}`,
    label: `${q + 1} квартал ${y}`,
  };
}

/** Предыдущий период того же вида */
export function previousRewardPeriod(kind: RewardPeriodKind, start: string): RewardPeriodRange {
  const [y, m] = start.split('-').map(Number) as [number, number];
  let mm = m - (kind === 'month' ? 1 : 3);
  let yy = y;
  if (mm < 1) {
    mm += 12;
    yy -= 1;
  }
  return rewardPeriodOf(kind, `${yy}-${pad2(mm)}-01`);
}

/** Строка расшифровки баллов: за какую запись и сколько */
export const RewardLine = z.object({
  action: RewardAction,
  points: z.number().int(),
  at: Ts,
  /** «ПРД-0007 Фильтр заявок по поставщику» или «ОБР-0012 …» */
  title: z.string(),
  path: z.string(),
});
export type RewardLine = z.infer<typeof RewardLine>;

const ActionCounts = z.object({
  submitted: z.number().int(),
  accepted: z.number().int(),
  released: z.number().int(),
  vote: z.number().int(),
  bug: z.number().int(),
});

/** Строка таблицы участников: место, баллы и из каких действий они сложились */
export const RewardStanding = z.object({
  userId: Id,
  fullName: z.string(),
  points: z.number().int(),
  place: z.number().int(),
  counts: ActionCounts,
});
export type RewardStanding = z.infer<typeof RewardStanding>;

/** Кандидат в номинацию «за предложение»: предложение периода с оценкой и голосами */
export const RewardCandidate = z.object({
  suggestionId: Id,
  number: z.string(),
  title: z.string(),
  status: SuggestionStatus,
  authorId: Id,
  authorName: z.string(),
  benefit: z.number().int().nullable(),
  votes: z.number().int(),
  /** Выпущено в этом периоде */
  releasedInPeriod: z.boolean(),
  priority: z.number().int(),
});
export type RewardCandidate = z.infer<typeof RewardCandidate>;

export const REWARD_AWARD_STATUSES = {
  approved: 'Назначена',
  paid: 'Выплачена',
  cancelled: 'Отменена',
} as const;
export type RewardAwardStatus = keyof typeof REWARD_AWARD_STATUSES;

export const RewardAward = z.object({
  id: Id,
  periodId: Id,
  periodLabel: z.string(),
  nomination: z.string(),
  nominationKind: RewardNominationKind,
  userId: Id,
  fullName: z.string(),
  suggestion: z.object({ id: Id, number: z.string(), title: z.string() }).nullable(),
  points: z.number().int().nullable(),
  /** Сумма — только тем, кто утверждает премии (специалист с уровнем «Все деньги», ДС-04); иначе null и amountHidden */
  amount: z.number().nullable(),
  amountHidden: z.boolean(),
  reason: z.string(),
  status: z.enum(['approved', 'paid', 'cancelled']),
  paidAt: Ts.nullable(),
  cancelReason: z.string().nullable(),
  createdAt: Ts,
  /** Премия просматривающего */
  mine: z.boolean(),
  /** Своя премия, которую человек ещё не открывал: она в его «Ждёт меня» */
  unseen: z.boolean(),
});
export type RewardAward = z.infer<typeof RewardAward>;

export const RewardPeriodSummary = z.object({
  id: Id,
  kind: RewardPeriodKind,
  start: Day,
  end: Day,
  label: z.string(),
  closedAt: Ts,
  closedByName: z.string().nullable(),
  note: z.string().nullable(),
  /**
   * Номинации из правил, сохранённых с итогами (КП-06): по ним добавляются премии к утверждённым итогам, даже если
   * правила с тех пор поменялись. Только тому, кто утверждает премии; остальным — null
   */
  nominations: z.array(RewardNominationView).nullable(),
  awards: z.array(RewardAward),
});
export type RewardPeriodSummary = z.infer<typeof RewardPeriodSummary>;

/** Вкладка «Премии»: правила, выбранный период, мои баллы, участники и кандидаты (специалистам), итоги */
export const RewardsView = z.object({
  rules: RewardRulesView,
  /** Текущий календарный период */
  current: z.object({ start: Day, end: Day, label: z.string() }),
  /** Период на экране: текущий, прошедший без итогов или закрытый */
  period: z.object({
    kind: RewardPeriodKind,
    start: Day,
    end: Day,
    label: z.string(),
    ended: z.boolean(),
    /** Итоги утверждены: баллы и участники — как на момент утверждения */
    closed: z.boolean(),
    closedPeriod: RewardPeriodSummary.nullable(),
  }),
  me: z.object({
    points: z.number().int(),
    /** Место среди участников с баллами; null — баллов пока нет */
    place: z.number().int().nullable(),
    of: z.number().int(),
    lines: z.array(RewardLine),
  }),
  /** Все участники с баллами — специалистам поддержки (КП-05); остальным — null */
  standings: z.array(RewardStanding).nullable(),
  /** Лучшие предложения периода — специалистам; остальным — null */
  candidates: z.array(RewardCandidate).nullable(),
  /** Прошедшие периоды без утверждённых итогов, в которых были баллы — специалистам */
  pending: z.array(z.object({ start: Day, end: Day, label: z.string() })),
  /** Утверждённые итоги последних периодов: доска лучших для всех сотрудников */
  history: z.array(RewardPeriodSummary),
  canManage: z.boolean(),
  /** Утвердить итоги, задать суммы и правила: специалист поддержки с уровнем «Все деньги» */
  canApprove: z.boolean(),
});
export type RewardsView = z.infer<typeof RewardsView>;

export const RewardStandingDetail = z.object({
  userId: Id,
  fullName: z.string(),
  points: z.number().int(),
  lines: z.array(RewardLine),
});
export type RewardStandingDetail = z.infer<typeof RewardStandingDetail>;

export const RewardAwardInput = z.object({
  nomination: z.string().trim().min(2).max(30),
  userId: Id,
  suggestionId: Id.nullable().optional(),
  /** Не указана — сумма номинации из правил */
  amount: Amount.nullable().optional(),
  reason: z.string().trim().min(5, 'Напишите, за что премия: это увидит победитель').max(500),
});
export type RewardAwardInput = z.infer<typeof RewardAwardInput>;

export const RewardCloseInput = z.object({
  /** Начало периода, итоги которого утверждаются */
  start: Day,
  note: z.string().trim().max(1000).nullable().optional(),
  awards: z.array(RewardAwardInput).max(30),
});
export type RewardCloseInput = z.infer<typeof RewardCloseInput>;

export const RewardCancelInput = z.object({
  reason: z.string().trim().min(5, 'Напишите причину отмены').max(500),
});
